import React, { useEffect, useState } from 'react';
import { Cache } from '../../../utils/models';

import Conditional from '../../common/conditional';
import Confirm from '../confirm';
import LoadingBadge from '../loading-badge';
import Modal from '../modal';
import WarningNotice from '../../custom-reports/custom-visual-inspection/warning-notice';

import * as utils from '../../../utils/utilities';

import useDataCache from '../../../hooks/useDataCache';
import toastService from '../../../services/toastService';
import classNames from './style.module.scss';
import config from '../../../utils/config';


interface ReportCacheNoticeProps {
	reportId: string;
}

const ReportCacheNotice: React.FC<ReportCacheNoticeProps> = ({ reportId }) => {
	const [isConfirmVisible, setConfirmVisible] = useState(false);
	const [cache, setCache] = useState<Cache | null>(null);

	const { data, error, onClearData, loading, refetch } = useDataCache(reportId);

	const handleClearCache = async () => {
		setCache(null);
		setConfirmVisible(false);

		onClearData();

		toastService.success('Clearing progress');
	};

	const handleContinueWithCache = (): void => {
		setCache(null);
		toastService.success('Picking up where you left off! Please check the preview for progress');
	};

	useEffect(() => {
		refetch();
	}, [refetch]);

	useEffect(() => {
		if (data && data.data) {
			setCache(data.data);
		}
	}, [data]);

	return (
				<>
					<Conditional isVisible={loading}>
						<div className="p-3 my-4 d-flex justify-content-end ">
							<LoadingBadge message="Fetching Progress..." />
						</div>
					</Conditional>

					<Conditional isVisible={!loading && !!data}>
						<Modal
							isVisible={isConfirmVisible}
							style={{ height: 'auto' }}
							onClose={() => setConfirmVisible(false)}
						>
							<div className="p-4">
								<Confirm
									title="Delete Report Progress?"
									message="Are you sure you want to delete the current progress? This action is NOT reversible!"
									onCancel={() => setConfirmVisible(false)}
									onConfirm={handleClearCache}
								/>
							</div>
						</Modal>

						{cache && (
							<div className={classNames['report-cache-confirm']}>
								<div className="p-3 bg-light border">
									<h2>
										<i className="bi bi-info-circle"></i>
										<span>Auto-Saved Version Found</span>
									</h2>

									<p>
										A saved version of this report has been found. It was last updated{' '}
										<span>
											{utils.formatTimeWithRelativeStamp(cache.timestamp as unknown as string)}
										</span>{' '}
										by{' '}
										<span>
											{cache.user?.firstName} {cache.user?.lastName}
										</span>
										. Do you want to continue with the auto-saved version?
									</p>

									<div className={classNames['report-cache-confirm-actions']}>
										<button
											type="button"
											className={classNames['report-cache-confirm-actions-yes']}
											onClick={handleContinueWithCache}
										>
											Yes, I want to continue
										</button>

										<Conditional isVisible={config.enableCacheDeletion}>
											<button
												type="button"
												className={classNames['report-cache-confirm-actions-no']}
												onClick={() => setConfirmVisible(true)}
											>
												No, I don't
											</button>
										</Conditional>
									</div>
								</div>
							</div>
						)}
					</Conditional>

					<Conditional isVisible={error}>
						<WarningNotice
							title="No progress found."
							body="No progress has been uploaded for this report."
							onAction={refetch}
						/>
					</Conditional>
				</>
			);
};

export default ReportCacheNotice;
